import React, { useEffect, useMemo, useRef, useState } from "react";

// region Imports - External libraries
import _ from "lodash";
import Loading from "react-fullscreen-loading";
// endregion Imports - External libraries
// region Imports - Shared
import { IParamQueryValues } from "@shared/interfaces/filter.interface";
import { FilterApplicationTypesID } from "@shared/constants/filter-application-types.enum";
import {
  VehicleFilterOptions,
  VehicleTypeFilterOption,
  PlantFilterOption,
  DriverFilterOption
} from "@shared/interfaces/vehicle-filter-options.interface";
import { Vehicle } from "@shared/interfaces/vehicle.interface";
import { StandardReturnObject } from "@shared/utils/schemas/standard-return-object";
// endregion Imports - Shared
// region Imports - Languages
import useTranslation from "src/translations/useTranslation";
import { FilterMessages, ToastMessages, VehicleMaintenanceMessages, VehiclesModuleMessages } from "@shared/languages/interfaces";
// endregion Imports - Languages
// region Imports - Services
import api from "@services/api";
// endregion Imports - Services
// region Imports - Hooks
import { useToast } from "@hooks/useToast";
// endregion Imports - Hooks
// region Imports - Organisms
import GenericQueryFilter, {
  IMultipleSelectionOptions,
  IOnFilterReturn
} from "@organisms/GenericQueryFilter";
import { ParamNameTypes } from "@shared/constants/param-name-types.enum";
// endregion Imports - Organisms

// region Interfaces
interface IProps {
  open: boolean;
  onClose: () => void;
  onFilter: (
    numberOfFilteredOptions: number,
    returnedData: Vehicle[]
  ) => void;
}
// endregion Interfaces

const QueryFilterVehicles: React.FC<IProps> = ({ open, onClose, onFilter }) => {

  // region Hooks
  const { t } = useTranslation();
  const { addToast } = useToast();
  // endregion Hooks
  // region States
  const [optionsVehicleTypeFilter, setOptionsVehicleTypeFilter] = useState<VehicleTypeFilterOption[]>([]);
  const [optionsPlantFilter, setOptionsPlantFilter] = useState<PlantFilterOption[]>([]);
  const [optionsDriverFilter, setOptionsDriverFilter] = useState<DriverFilterOption[]>([]);
  const [optionsExternalIdFilter, setOptionsExternalIdFilter] = useState<string[]>([]);

  const [loadingVehicleTypeFilter, setLoadingVehicleTypeFilter] = useState(true);
  const [loadingPlantFilter, setLoadingPlantFilter] = useState(true);
  const [loadingDriverFilter, setLoadingDriverFilter] = useState(true);
  const [loadingExternalIdFilter, setLoadingExternalIdFilter] = useState(true);
  const [loadingActiveStatusFilter, setLoadingActiveStatusFilter] = useState(true);
  const [loadingMaintenanceStatusFilter, setLoadingMaintenanceStatusFilter] = useState(true);

  const [loadingVehicles, setLoadingVehicles] = useState(false);
  // endregion useState
  // region Constants
  const paramNameVehicleTypes = ParamNameTypes.ID_VEHICLE_TYPES;
  const paramNamePlants = ParamNameTypes.ID_PLANTS;
  const paramNameDrivers = ParamNameTypes.ID_DRIVERS;
  const paramNameExternalId = ParamNameTypes.EXTERNAL_ID;
  const paramNameActiveStatus = ParamNameTypes.ACTIVE_STATUS;
  const paramNameMaintenanceStatus = ParamNameTypes.MAINTENANCE_STATUS;
  const paramValueNotIntegrated = "onlyNotIntegrated";

  const vehicleTypeFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingVehicleTypeFilter,
    labelName: t(VehiclesModuleMessages.vehicleType),
    paramName: paramNameVehicleTypes,
    values: optionsVehicleTypeFilter.map(
      (type) => ({
        value: type.id_vehicle_type!,
        label: type.description!
      })
    )
  }), [loadingVehicleTypeFilter, optionsVehicleTypeFilter, paramNameVehicleTypes, t]);

  const plantFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingPlantFilter,
    labelName: t(FilterMessages.optionPlants),
    paramName: paramNamePlants,
    values: optionsPlantFilter.map(
      (plant) => ({
        value: plant.id_plant!,
        label: plant.name!
      })
    )
  }), [loadingPlantFilter, optionsPlantFilter, paramNamePlants, t]);

  const driverFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingDriverFilter,
    labelName: t(VehiclesModuleMessages.defaultDriver),
    paramName: paramNameDrivers,
    values: optionsDriverFilter.map(
      (driver) => ({
        value: driver.id_driver!,
        label: driver.name!
      })
    )
  }), [loadingDriverFilter, optionsDriverFilter, paramNameDrivers, t]);

  const activeStatusFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingActiveStatusFilter,
    labelName: t(VehiclesModuleMessages.status),
    paramName: paramNameActiveStatus,
    values: [
      {
        value: "true",
        label: t(VehiclesModuleMessages.active)
      },
      {
        value: "false",
        label: t(VehiclesModuleMessages.inactive)
      }
    ]
  }), [loadingActiveStatusFilter, paramNameActiveStatus, t]);

  const maintenanceStatusFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingMaintenanceStatusFilter,
    labelName: t(VehiclesModuleMessages.maintenance),
    paramName: paramNameMaintenanceStatus,
    values: [
      {
        value: "true",
        label: t(VehicleMaintenanceMessages.inMaintenance)
      },
      {
        value: "false",
        label: t(VehicleMaintenanceMessages.outOfMaintenance)
      }
    ]
  }), [loadingMaintenanceStatusFilter, paramNameMaintenanceStatus, t]);

  const externalIdFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingExternalIdFilter,
    labelName: t(FilterMessages.optionExternalId),
    paramName: paramNameExternalId,
    values: optionsExternalIdFilter.map((externalId) =>
      (externalId === paramValueNotIntegrated ?
        {value: externalId, label: t(FilterMessages.noExternalId) } : { value: externalId, label: externalId }))
  }), [loadingExternalIdFilter, optionsExternalIdFilter, t, paramNameExternalId, paramValueNotIntegrated]);

  // endregion Constants
  // region Refs
  const paramsFilterRef = useRef<IParamQueryValues[]>([]);
  // endregion Refs
  // region Functions

  /**
   * Load filter options from the API.
   */
  const loadFilterOptions = async () => {

    try {

      const { data }: { data: StandardReturnObject<VehicleFilterOptions> } = await api.get("vehicles/filter-options");

      if (data.status === "success") {
        setOptionsVehicleTypeFilter(data.result?.vehicleTypes ?? []);
        setOptionsPlantFilter(data.result?.plants ?? []);
        setOptionsDriverFilter(data.result?.drivers ?? []);

        if (data.result?.externalIds) {
          let options = data.result.externalIds;

          options.unshift(paramValueNotIntegrated);

          setOptionsExternalIdFilter(options);
        }
      } else {
        addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
      }

    } catch (error) {
      if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });
    } finally {
      setLoadingVehicleTypeFilter(false);
      setLoadingPlantFilter(false);
      setLoadingDriverFilter(false);
      setLoadingExternalIdFilter(false);
      setLoadingActiveStatusFilter(false);
      setLoadingMaintenanceStatusFilter(false);
    }
  };

  const handleFilter = async (paramsData: IParamQueryValues[]) => {

    setLoadingVehicles(true);

    paramsFilterRef.current = paramsData;

    try {

      let paramWithoutExternalId = _.cloneDeep(paramsData);

      const foundParam = paramWithoutExternalId.some(
        (param) => param.paramName === paramNameExternalId && param.paramValue.includes(paramValueNotIntegrated)
      );

      let params = paramWithoutExternalId.find(
        (item) => (item.paramName === paramNameExternalId && item.paramValue.includes(paramValueNotIntegrated))
      );

      if (Array.isArray(params?.paramValue)) {
        params!.paramValue = params!.paramValue.filter(value => value !== paramValueNotIntegrated);
      }

      const mappedParams = paramWithoutExternalId.reduce((acc, param) => {
        if (param.paramName === paramNameActiveStatus || param.paramName === paramNameMaintenanceStatus) {
          acc[param.paramName] = Array.isArray(param.paramValue)
            ? param.paramValue.map(val => val === "true")
            : [param.paramValue === "true"];
        } else {
          acc[param.paramName] = param.paramValue;
        }

        return acc;
      }, {} as { [key: string]: string | string[] | boolean[] });

      const { data } = await api.post("vehicles/get-filtered-by", {
        ...mappedParams,
        onlyNotIntegrated: foundParam
      });

      const validParamsData = paramsData.filter((item) => !_.isEmpty(item.paramValue));

      if (data.status !== "success") addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });

      onFilter(
        validParamsData.length,
        data.result ?? [] as Vehicle[]
      );

      return { status: "success" } as IOnFilterReturn;
    } catch (error) {
      if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });

      return { status: "error" } as IOnFilterReturn;
    } finally {
      setLoadingVehicles(false);
    }
  };
  // endregion Functions
  // region Effects

  // Load options from the API
  useEffect(() => {

    loadFilterOptions().then();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // endregion Effects

  return (
    <>
      <Loading loading={loadingVehicles} />
      <GenericQueryFilter
        open={open}
        onClose={onClose}
        applicationTypeID={FilterApplicationTypesID.VEHICLES}
        onFilter={(paramsData) => handleFilter(paramsData)}
        multipleSelectionOptions={[
          vehicleTypeFilterOptions,
          plantFilterOptions,
          driverFilterOptions,
          externalIdFilterOptions,
          activeStatusFilterOptions,
          maintenanceStatusFilterOptions
        ]}
      />
    </>
  );
};

export default QueryFilterVehicles;
